/**
 * Translation and explainer utilities for build process transparency
 * Converts technical jargon into user-friendly explanations
 */

export interface InstanceInfo {
  description: string;
  specs: string;
  cost: string;
  useCase: string;
}

export interface ErrorTranslation {
  summary: string;
  actionable?: string;
}

/**
 * Explains why a specific instance type was chosen
 */
export function getInstanceExplanation(instanceType: string, provider: string): InstanceInfo {
  const explanations: Record<string, InstanceInfo> = {
    // AWS GPU instances
    'g4dn.xlarge': {
      description: 'GPU-powered instance with NVIDIA T4 graphics card',
      specs: '4 vCPUs, 16 GB RAM, 1x NVIDIA T4 (16GB GPU memory)',
      cost: '~$0.526/hour (~$0.01 per test)',
      useCase: 'Selected because your template includes GPU packages (RAPIDS, PyTorch, or TensorFlow). This ensures GPU drivers and CUDA work correctly.'
    },
    'g4dn.2xlarge': {
      description: 'Larger GPU instance with NVIDIA T4',
      specs: '8 vCPUs, 32 GB RAM, 1x NVIDIA T4 (16GB GPU memory)',
      cost: '~$0.752/hour (~$0.01 per test)',
      useCase: 'Selected for GPU workloads that need more CPU/memory alongside the GPU.'
    },
    't3.micro': {
      description: 'Minimal CPU-only instance for basic validation',
      specs: '2 vCPUs, 1 GB RAM',
      cost: '~$0.0104/hour (~$0.0002 per test)',
      useCase: 'Selected for lightweight testing of non-GPU packages. Very cost-effective for basic validation.'
    },
    't3.small': {
      description: 'Small CPU instance for standard workloads',
      specs: '2 vCPUs, 2 GB RAM',
      cost: '~$0.0208/hour (~$0.0004 per test)',
      useCase: 'Selected for CPU-based package testing with moderate memory requirements.'
    },

    // GCP GPU instances
    'n1-standard-4': {
      description: 'Standard instance, often paired with GPU for testing',
      specs: '4 vCPUs, 15 GB RAM (+ T4 GPU if needed)',
      cost: '~$0.19/hour + $0.35/hour for GPU (~$0.01 per test)',
      useCase: 'Selected for GPU testing on Google Cloud. Good balance of CPU/RAM for deep learning workloads.'
    },
    'n1-standard-2': {
      description: 'Medium CPU instance',
      specs: '2 vCPUs, 7.5 GB RAM',
      cost: '~$0.095/hour (~$0.002 per test)',
      useCase: 'Selected for CPU-based testing with moderate compute needs.'
    },
    'e2-micro': {
      description: 'Minimal instance for basic validation',
      specs: '2 vCPUs (shared), 1 GB RAM',
      cost: '~$0.0084/hour (~$0.0001 per test)',
      useCase: 'Most cost-effective option for simple package installation testing.'
    }
  };

  return explanations[instanceType] || {
    description: `${provider} instance type ${instanceType}`,
    specs: 'Specs vary by cloud provider',
    cost: 'Cost depends on instance type and region',
    useCase: 'Selected based on your template requirements (GPU/CPU, memory needs).'
  };
}

/**
 * Explains the purpose of common commands
 */
export function getCommandPurpose(command: string): string {
  // Handle undefined/null command
  if (!command || typeof command !== 'string') {
    return 'Processing build step...';
  }

  // GPU checks
  if (command.includes('nvidia-smi')) {
    return 'Verifying GPU is accessible and drivers are working';
  }

  // Package manager checks
  if (command.includes('conda') && command.includes('--version')) {
    return 'Checking that Conda package manager is installed';
  }
  if (command.includes('pip') && command.includes('--version')) {
    return 'Checking that pip package installer is available';
  }
  if (command.includes('uv') && command.includes('--version')) {
    return 'Checking that uv (fast Python package manager) is installed';
  }

  // Environment setup
  if (command.includes('conda config')) {
    return 'Configuring Conda settings for automated installation';
  }
  if (command.includes('conda create') || command.includes('conda env create')) {
    return 'Creating isolated Python environment for your packages';
  }
  if (command.includes('conda activate')) {
    return 'Activating the Python environment';
  }

  // Package installation
  if (command.includes('conda install') || command.includes('mamba install')) {
    const packages = command.match(/install\s+(.+?)(?:\s+-|$)/)?.[1] || 'packages';
    return `Installing ${packages} via Conda package manager`;
  }
  if (command.includes('pip install')) {
    const packages = command.match(/install\s+(.+?)(?:\s+-|$)/)?.[1] || 'packages';
    return `Installing ${packages} via pip`;
  }
  if (command.includes('uv pip install')) {
    return 'Installing Python packages using uv (10-100x faster than pip)';
  }

  // RAPIDS-specific
  if (command.includes('cudf') || command.includes('cuml') || command.includes('cugraph')) {
    return 'Installing RAPIDS GPU-accelerated data science libraries';
  }

  // PyTorch/TensorFlow
  if (command.includes('torch') && !command.includes('torchvision')) {
    return 'Installing PyTorch deep learning framework';
  }
  if (command.includes('tensorflow')) {
    return 'Installing TensorFlow machine learning framework';
  }

  // Verification commands
  if (command.includes('python') && command.includes('-c')) {
    if (command.includes('import')) {
      return 'Verifying that installed packages can be imported';
    }
    return 'Running Python test command';
  }

  // System commands
  if (command.includes('apt') || command.includes('apt-get')) {
    return 'Installing system-level dependencies';
  }
  if (command.includes('which') || command.includes('whereis')) {
    return 'Locating command on the system';
  }

  return 'Executing custom command from your template';
}

/**
 * Translates technical errors into user-friendly language
 */
export function translateError(stderr: string, command: string): ErrorTranslation {
  const error = stderr.toLowerCase();

  // Conda not found
  if (error.includes('conda: not found') || error.includes('conda: no such file')) {
    return {
      summary: 'Conda package manager is not installed at the expected location',
      actionable: 'The AI is searching for Conda in alternative locations and will retry automatically'
    };
  }

  // Permission denied
  if (error.includes('permission denied')) {
    return {
      summary: 'Command requires administrator privileges',
      actionable: 'The AI will retry with proper permissions (sudo)'
    };
  }

  // Network/download errors
  if (error.includes('could not resolve host') || error.includes('connection refused')) {
    return {
      summary: 'Network connection issue while downloading packages',
      actionable: 'This is usually temporary. The system will retry automatically.'
    };
  }
  if (error.includes('connection timed out') || error.includes('timeout')) {
    return {
      summary: 'Package download took too long and timed out',
      actionable: 'This often happens with large GPU packages. The system will increase timeout and retry.'
    };
  }

  // Disk space
  if (error.includes('no space left on device')) {
    return {
      summary: 'The virtual machine ran out of disk space',
      actionable: 'You may need to reduce package count or use a different base image with more storage'
    };
  }

  // Package conflicts
  if (error.includes('conflict') || error.includes('incompatible')) {
    return {
      summary: 'Package version conflict detected',
      actionable: 'The AI is analyzing package dependencies to suggest compatible versions'
    };
  }

  // Module not found
  if (error.includes('modulenotfounderror') || error.includes('no module named')) {
    const moduleName = stderr.match(/no module named ['"](.+?)['"]/i)?.[1] || 'package';
    return {
      summary: `Python cannot find the ${moduleName} module`,
      actionable: 'This means the package didn\'t install correctly. The AI will diagnose the installation issue.'
    };
  }

  // CUDA errors
  if (error.includes('cuda') && (error.includes('not found') || error.includes('error'))) {
    return {
      summary: 'GPU/CUDA error - graphics drivers or CUDA toolkit issue',
      actionable: 'The AI will verify GPU availability and suggest compatible CUDA versions'
    };
  }

  // Conda solver errors
  if (error.includes('could not solve') || error.includes('no packages found')) {
    return {
      summary: 'Conda could not find compatible package versions',
      actionable: 'The AI will try alternative channels (conda-forge) or suggest specific version combinations'
    };
  }

  // Generic command not found
  if (error.includes('command not found') || error.includes('not recognized')) {
    const cmd = command.split(' ')[0];
    return {
      summary: `The command "${cmd}" is not installed or not in the system PATH`,
      actionable: 'The AI will locate the correct path or suggest installing the missing tool'
    };
  }

  // Exit code errors
  if (error.includes('exit status') || error.includes('returned non-zero')) {
    return {
      summary: 'Command completed but reported an error',
      actionable: 'The AI is analyzing the command output to determine the root cause'
    };
  }

  // Default case
  return {
    summary: 'Command failed with an error',
    actionable: 'The AI is analyzing the error details to suggest a fix'
  };
}

/**
 * Provides actionability for different error types
 */
export function getErrorActionability(stderr: string): string | null {
  const error = stderr.toLowerCase();

  if (error.includes('quota') || error.includes('throttling')) {
    return 'This is a cloud provider limit. The system will wait and retry, or you can try a different region.';
  }

  if (error.includes('unauthorized') || error.includes('403') || error.includes('401')) {
    return 'This is a permissions issue. Check that your cloud credentials are configured correctly.';
  }

  if (error.includes('not found') && !error.includes('command not found')) {
    return 'The resource (image, file, or package) does not exist. The AI will suggest alternatives.';
  }

  return null;
}

/**
 * Gets glossary definition for technical terms
 */
export function getGlossaryDefinition(term: string): string {
  const glossary: Record<string, string> = {
    'conda': 'Package manager for Python that handles complex dependencies, similar to npm for JavaScript. Especially good at managing scientific/ML libraries.',
    'mamba': 'Faster reimplementation of Conda written in C++. Solves dependencies much quicker (seconds vs minutes).',
    'micromamba': 'Minimal, standalone version of Mamba. Very lightweight and fast.',
    'pip': 'Standard Python package installer. Simpler than Conda but doesn\'t handle non-Python dependencies well.',
    'uv': 'Ultra-fast Python package manager (10-100x faster than pip). Written in Rust. New standard for Python projects.',
    'nvidia-smi': 'NVIDIA System Management Interface - command that shows GPU status, memory usage, and confirms drivers work.',
    'SSM': 'AWS Systems Manager - lets you securely connect to virtual machines without SSH keys. More secure than traditional remote access.',
    'IAP': 'Google Identity-Aware Proxy - secure way to connect to VMs without public IPs. Like SSM for Google Cloud.',
    'Packer': 'HashiCorp tool that creates identical machine images for multiple cloud providers from one template. Prevents "works on AWS but not GCP" issues.',
    'AMI': 'Amazon Machine Image - pre-configured virtual machine template for AWS. Contains OS, software, and settings.',
    'GCE Image': 'Google Compute Engine Image - virtual machine snapshot for Google Cloud. Similar to AWS AMI.',
    'RAPIDS': 'NVIDIA\'s GPU-accelerated data science libraries. Makes Pandas, scikit-learn work 10-100x faster on GPUs.',
    'CUDA': 'NVIDIA\'s parallel computing platform. Lets programs use GPU power instead of just CPU.',
    'cuDF': 'GPU version of Pandas DataFrame library (part of RAPIDS). Processes huge datasets much faster.',
    'PyTorch': 'Facebook\'s deep learning framework. Popular for research and production ML models.',
    'TensorFlow': 'Google\'s machine learning framework. Used for production ML at scale.',
    'vCPU': 'Virtual CPU - compute power in cloud instances. Usually 1 vCPU = 0.5 physical CPU cores.',
    'T4': 'NVIDIA Tesla T4 GPU - datacenter graphics card designed for AI inference. 16GB memory, good price/performance.',
    'A100': 'NVIDIA\'s flagship datacenter GPU. Much more powerful than T4, used for training large AI models.',
    'g4dn': 'AWS instance family with NVIDIA T4 GPUs. Cost-effective for ML inference and training small models.',
    'n1': 'Google Cloud\'s first-generation general-purpose instances. Reliable and well-priced.',
    'Pre-Flight Testing': 'Testing commands in temporary VMs before the full image build. Catches errors in 1-4 minutes instead of 20-40 minutes.',
    'Image Build': 'Creating the final virtual machine template with all your software installed. Takes 20-40 minutes depending on packages.',
  };

  return glossary[term] || `${term} - technical term used in cloud computing`;
}

/**
 * Formats duration in human-readable format
 */
export function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${Math.round(seconds)}s`;
  } else if (seconds < 3600) {
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  } else {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }
}

/**
 * Gets estimated time remaining based on progress
 */
export function getTimeEstimate(currentCmd: number, totalCmds: number, elapsed: number): string {
  if (currentCmd === 0 || totalCmds === 0) {
    return 'Estimating...';
  }

  const avgTimePerCmd = elapsed / currentCmd;
  const remainingCmds = totalCmds - currentCmd;
  const estimatedRemaining = avgTimePerCmd * remainingCmds;

  if (estimatedRemaining < 10) {
    return 'Almost done...';
  }

  return `~${formatDuration(estimatedRemaining)} remaining`;
}
